import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, onSnapshot, orderBy } from "firebase/firestore";
import { CheckCircle, ChevronRight, Package, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Spinner from './Spinner.js';
import ListFilterBar from './components/ListFilterBar.js';
import StatusBadge from './components/StatusBadge.js';

const DeliveredList = ({ db, user }) => {
  const navigate = useNavigate();
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterWarehouse, setFilterWarehouse] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterType, setFilterType] = useState('All');

  // --- RESPONSIVE STATE ---
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth <= 768;

  useEffect(() => {
    setLoading(true);
    // Fetch dispatches that are Delivered
    const q = query(
      collection(db, "dispatches"), 
      where("dispatchStatus", "==", "delivered"),
      orderBy("updatedAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const deliveryList = querySnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
      setDeliveries(deliveryList);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching Delivered list: ", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [db]);

  const warehouseOptions = useMemo(() => ['All', ...Array.from(new Set(deliveries.map((delivery) => delivery.warehouse).filter(Boolean)))], [deliveries]);
  const statusOptions = useMemo(() => ['All', ...Array.from(new Set(deliveries.map((delivery) => delivery.dispatchStatus).filter(Boolean)))], [deliveries]);
  const typeOptions = useMemo(() => ['All', ...Array.from(new Set(deliveries.map((delivery) => delivery.typeOfMOC).filter(Boolean)))], [deliveries]);
  const filterFields = useMemo(() => ([
    { key: 'searchTerm', type: 'search', label: 'Customer Search', placeholder: 'Search by Customer, MOC, DC, or Vehicle No...', minWidth: '320px' },
    { key: 'filterType', type: 'select', label: 'MOC Type', options: typeOptions.map((option) => ({ value: option, label: option === 'All' ? 'All Types' : option })) },
    { key: 'filterStatus', type: 'select', label: 'Status', options: statusOptions.map((option) => ({ value: option, label: option === 'All' ? 'All Statuses' : option })) },
    { key: 'filterWarehouse', type: 'select', label: 'Warehouse', options: warehouseOptions.map((option) => ({ value: option, label: option === 'All' ? 'All Warehouses' : option })) }
  ]), [statusOptions, typeOptions, warehouseOptions]);

  const handleFilterChange = (key, value) => {
    if (key === 'searchTerm') setSearchTerm(value);
    if (key === 'filterType') setFilterType(value);
    if (key === 'filterStatus') setFilterStatus(value);
    if (key === 'filterWarehouse') setFilterWarehouse(value);
  };

  const filteredDeliveries = useMemo(() => {
    return deliveries.filter(d => {
      const matchesSearch =
        d.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.mocNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.challanNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.vehicleNumber?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesWarehouse = filterWarehouse === 'All' || d.warehouse === filterWarehouse;
      const matchesStatus = filterStatus === 'All' || d.dispatchStatus === filterStatus;
      const matchesType = filterType === 'All' || d.typeOfMOC === filterType;
      return matchesSearch && matchesWarehouse && matchesStatus && matchesType;
    });
  }, [deliveries, searchTerm, filterWarehouse, filterStatus, filterType]);

  const formatDateTime = (value) => {
      if (!value) return '---';
      const date = value?.toDate ? value.toDate() : new Date(value);
      if (Number.isNaN(date.getTime())) return '---';
      return date.toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
  };
  
  if (loading) return <Spinner />;

  return (
    <div style={{ padding: isMobile ? '10px' : '0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
        <div>
          <h2 style={{ margin: 0 }}>Delivered Orders</h2>
          <p style={{ color: '#64748b', marginTop: '5px' }}>Log of all successfully delivered materials and PODs.</p>
        </div>
      </div>

      <ListFilterBar
        fields={filterFields}
        values={{ searchTerm, filterType, filterStatus, filterWarehouse }}
        onFieldChange={handleFilterChange}
      />

      {deliveries.length === 0 ? (
        <div style={{ padding: '50px', textAlign: 'center', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
          <Package size={48} color="#cbd5e1" style={{ marginBottom: '15px' }} />
          <h3 style={{ color: '#374151' }}>No deliveries yet.</h3>
          <p style={{ color: '#64748b' }}>Completed deliveries will appear here.</p>
        </div>
      ) : (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: '#f9fafb' }}>
              <tr>
                <th style={thStyle}>Delivery Date</th>
                <th style={{...thStyle, textAlign:'left'}}>Customer / Destination</th>
                <th style={thStyle}>DC No.</th>
                <th style={thStyle}>Receiver Info</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredDeliveries.map((d) => (
                <tr 
                  key={d.id} 
                  style={{ borderBottom: '1px solid #e5e7eb', cursor: 'pointer' }}
                  onClick={() => navigate(`/ops/delivered/${d.id}`)}
                  className="hover-highlight"
                >
                  <td style={tdStyle}>
                    <div style={{fontWeight: 'bold'}}>
                      {formatDateTime(d.deliveredAt?.toDate ? d.deliveredAt.toDate() : (d.deliveryDetails?.deliveryDate || d.updatedAt?.toDate?.() || d.updatedAt))}
                    </div>
                  </td>
                  <td style={{...tdStyle, textAlign:'left'}}>
                    <div style={{fontWeight: 'bold', fontSize: '14px'}}>{d.customerName}</div>
                    <div style={{fontSize: '11px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px'}}>
                        <MapPin size={12} /> {d.shipToAddress?.substring(0, 40) || d.shippingAddress?.substring(0, 40)}...
                    </div>
                  </td>
                  <td style={tdStyle}><strong>{d.challanNo}</strong></td>
                  <td style={tdStyle}>
                      <div style={{fontSize: '13px'}}>{d.deliveryDetails?.receiverName || '---'}</div>
                      <div style={{fontSize: '11px', color: '#64748b'}}>{d.deliveryDetails?.receiverContact || ''}</div>
                  </td>
                  <td style={tdStyle}>
                    <StatusBadge status="delivered" iconSize={12} />
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', color: '#2563eb', fontWeight: '600' }}>
                      View <ChevronRight size={16} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const thStyle = { padding: '15px', textAlign: 'center', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' };
const tdStyle = { padding: '15px', fontSize: '13px', textAlign: 'center' };

export default DeliveredList;
